import { createMemo } from 'solid-js';
import { distributions, distributionsFrom } from '~data';
import { accounts } from '~/stores/portfolio-store';
import { transactions, type Transaction } from '~/stores/transactions-store';
import { unitsBefore } from '~/stores/positions';

// What was actually received, rather than what a yield implies.
//
// Every distribution the funds paid is on file with its ex-date and per-unit
// amount, and every trade is on file with its date and units. Crossing the two
// gives the real payout history — and it needs no price at all, so this works
// while market value is still N/A.

export interface DividendPayment {
    exDate: string;
    payDate: string | null;
    accountId: string;
    symbol: string;
    /** Units held when entitlement was decided. */
    units: number;
    perUnit: number;
    amount: number;
}

/**
 * Units held at an ex-date are the trades settled *before* it: buying on the
 * ex-date is buying without the distribution, so the comparison is strictly
 * less-than, not less-than-or-equal. A sale on the ex-date cuts the same way and
 * for the same reason — the seller is still on the register the day before, so
 * the payment is theirs.
 */
export const payments = createMemo<DividendPayment[]>(() => {
    const owned = new Set(accounts().map((a) => a.id));

    // Group trades by account and symbol so each distribution is matched against
    // one position rather than rescanning the whole blotter.
    const traded = new Map<string, Transaction[]>();
    for (const t of transactions) {
        if (!owned.has(t.accountId)) continue;
        const key = `${t.accountId} ${t.symbol}`;
        const list = traded.get(key) ?? [];
        list.push(t);
        traded.set(key, list);
    }

    const out: DividendPayment[] = [];
    for (const [key, list] of traded) {
        const [accountId, symbol] = key.split(' ');
        for (const d of distributions(symbol)) {
            const units = unitsBefore(list, d.exDate);
            if (units <= 0) continue;
            out.push({ exDate: d.exDate, payDate: d.payDate, accountId, symbol, units, perUnit: d.amount, amount: units * d.amount });
        }
    }

    return out.sort((a, b) => b.exDate.localeCompare(a.exDate) || a.symbol.localeCompare(b.symbol));
});

export const totalReceived = createMemo(() => payments().reduce((sum, p) => sum + p.amount, 0));

/** Oldest and newest ex-dates that produced a payment. */
export const range = createMemo(() => {
    const all = payments();
    if (all.length === 0) return null;
    return { from: all[all.length - 1].exDate, to: all[0].exDate };
});

export interface PeriodTotal {
    period: string;
    amount: number;
    payments: number;
}

const bucket = (all: DividendPayment[], key: (p: DividendPayment) => string): PeriodTotal[] => {
    const totals = new Map<string, PeriodTotal>();
    for (const p of all) {
        const period = key(p);
        const entry = totals.get(period) ?? { period, amount: 0, payments: 0 };
        entry.amount += p.amount;
        entry.payments += 1;
        totals.set(period, entry);
    }
    return [...totals.values()].sort((a, b) => b.period.localeCompare(a.period));
};

/** Bucketed on pay date where there is one — that is when the cash arrived —
    falling back to ex-date. */
export const byMonth = createMemo(() => bucket(payments(), (p) => (p.payDate ?? p.exDate).slice(0, 7)));

export const byYear = createMemo(() => bucket(payments(), (p) => (p.payDate ?? p.exDate).slice(0, 4)));

/** The monthly totals for one account, newest first. Not memoised: one account
    is on screen at a time, and a cache per account would hold three answers
    nobody is looking at. */
export const byMonthIn = (accountId: string): PeriodTotal[] =>
    bucket(
        payments().filter((p) => p.accountId === accountId),
        (p) => (p.payDate ?? p.exDate).slice(0, 7),
    );

/**
 * The monthly totals as a series: oldest first, and every month present.
 *
 * `byMonth` is a table and holds only the months something was paid in. A chart
 * cannot use it directly, because a month with no payment is not a missing
 * observation — it is a month you were paid nothing, and it belongs on the line
 * at zero.
 *
 * Drawn from the table instead, the line would join March to July with one
 * segment and slope gently between them, which reads as three months of steady
 * income where there were three months of none. The gap is the finding.
 */
export const fillMonths = (totals: readonly PeriodTotal[]): PeriodTotal[] => {
    if (totals.length === 0) return [];

    // byMonth is newest first, so the ends are the other way round. Taken by
    // extent rather than by trusting the order, because a series that runs
    // backwards would walk away from its own end forever.
    const periods = totals.map((t) => t.period);
    const first = periods.reduce((lo, p) => (p < lo ? p : lo));
    const last = periods.reduce((hi, p) => (p > hi ? p : hi));
    const paid = new Map(totals.map((t) => [t.period, t]));

    const out: PeriodTotal[] = [];
    let [year, month] = first.split('-').map(Number);
    if (!year || !month) return [...totals].reverse();

    // Bounded. The exit condition is reaching `last`, and a malformed period
    // that never matches would otherwise spin the tab rather than draw a short
    // chart — the wrong failure by a wide margin. Two centuries of months is far
    // past any real blotter and far short of a hang.
    for (let guard = 0; guard < 2400; guard += 1) {
        const period = `${year}-${String(month).padStart(2, '0')}`;
        out.push(paid.get(period) ?? { period, amount: 0, payments: 0 });
        if (period >= last) break;
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }
    return out;
};

export const monthlySeries = createMemo<PeriodTotal[]>(() => fillMonths(byMonth()));

/** A month against the month before it. `pct` is null where there is nothing to
    divide by — see growthFrom. */
export interface MonthGrowth {
    period: string;
    /** Change on the month before, in percent, or null where it paid nothing. */
    pct: number | null;
    now: number;
    then: number;
}

/**
 * How much each month grew on the one before it.
 *
 * Null rather than infinity where the previous month paid nothing. Growth from
 * zero has no percentage, and a made-up large number there would be the biggest
 * point on the chart and mean nothing at all.
 */
export const growthFrom = (months: readonly PeriodTotal[]): MonthGrowth[] => {
    const out: MonthGrowth[] = [];
    for (let i = 1; i < months.length; i += 1) {
        const now = months[i].amount;
        const then = months[i - 1].amount;
        out.push({ period: months[i].period, pct: then > 0 ? ((now - then) / then) * 100 : null, now, then });
    }
    return out;
};

export const growthSeries = createMemo<MonthGrowth[]>(() => growthFrom(monthlySeries()));

export const bySymbol = createMemo(() => {
    const totals = new Map<string, { symbol: string; amount: number; payments: number }>();
    for (const p of payments()) {
        const entry = totals.get(p.symbol) ?? { symbol: p.symbol, amount: 0, payments: 0 };
        entry.amount += p.amount;
        entry.payments += 1;
        totals.set(p.symbol, entry);
    }
    return [...totals.values()].sort((a, b) => b.amount - a.amount);
});

/**
 * Symbols whose distribution history starts after the first buy — their totals
 * are missing whatever was paid in between.
 *
 * Worth surfacing rather than quietly under-reporting: the number would still
 * look like a real total. Empty today, because the scrape goes back to each
 * fund's inception, but it guards a partial refetch.
 */
export const incompleteSymbols = createMemo(() => {
    const earliestBuy = new Map<string, string>();
    const owned = new Set(accounts().map((a) => a.id));
    for (const t of transactions) {
        if (!owned.has(t.accountId)) continue;
        const current = earliestBuy.get(t.symbol);
        if (!current || t.date < current) earliestBuy.set(t.symbol, t.date);
    }

    const gaps: string[] = [];
    for (const [symbol, first] of earliestBuy) {
        const from = distributionsFrom(symbol);
        if (from == null || from > first) gaps.push(symbol);
    }
    return gaps.sort();
});
